import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ApiResponseEnvelope,
  PaginatedListResult,
  PaginatedQueryOptions,
} from '@doctor-scheduling/shared/models';
import { API_BASE_URL } from './api-base-url.token';
import { buildListHttpParams } from './list-query.util';

/** Doctor row including linked user profile fields. */
export interface DoctorListItem {
  id: string;
  userId: string;
  clinicId: string;
  specialization: string;
  qualification: string;
  experience: number | null;
  licenseNumber: string;
  consultationDuration: number;
  consultationFee: string | null;
  bio: string | null;
  isAcceptingAppointments: boolean;
  user: {
    id: string;
    email: string;
    username: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

/** HTTP client for doctor CRUD within the current clinic. */
@Injectable({
  providedIn: 'root',
})
export class DoctorApi {
  /** Angular HTTP client used for API requests. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for doctor routes under `/api/v1/doctors`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/doctors`;

  /**
   * Lists doctors with Eaze-style page/sort/filter query params.
   * @param opts - Pagination, sort, search, and filter maps
   */
  findAll(
    opts?: PaginatedQueryOptions
  ): Observable<ApiResponseEnvelope<PaginatedListResult<DoctorListItem>>> {
    return this.http.get<ApiResponseEnvelope<PaginatedListResult<DoctorListItem>>>(
      this.baseUrl,
      { params: buildListHttpParams(opts) }
    );
  }

  /**
   * Loads a single doctor by id.
   * @param id - Doctor UUID
   */
  findOne(id: string): Observable<ApiResponseEnvelope<DoctorListItem>> {
    return this.http.get<ApiResponseEnvelope<DoctorListItem>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Creates a doctor and associated user account.
   */
  create(data: {
    clinicId?: string;
    email: string;
    username: string;
    firstName: string;
    lastName: string;
    specialization: string;
    qualification: string;
    experience?: number;
    licenseNumber: string;
    consultationDuration?: number;
    consultationFee?: number;
    bio?: string;
    isAcceptingAppointments?: boolean;
  }): Observable<ApiResponseEnvelope<DoctorListItem>> {
    return this.http.post<ApiResponseEnvelope<DoctorListItem>>(this.baseUrl, data);
  }

  /**
   * Updates doctor and linked user fields.
   */
  update(
    id: string,
    data: {
      email?: string;
      firstName?: string;
      lastName?: string;
      specialization?: string;
      qualification?: string;
      experience?: number;
      licenseNumber?: string;
      consultationDuration?: number;
      consultationFee?: number;
      bio?: string;
      isAcceptingAppointments?: boolean;
    }
  ): Observable<ApiResponseEnvelope<DoctorListItem>> {
    return this.http.put<ApiResponseEnvelope<DoctorListItem>>(`${this.baseUrl}/${id}`, data);
  }

  /**
   * Soft-deletes a doctor by id.
   */
  delete(id: string): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    return this.http.delete<ApiResponseEnvelope<{ success: boolean }>>(`${this.baseUrl}/${id}`);
  }
}
